import type { ChunkStreamPutTransport } from "./chunkStreamUploader";
import type { SmallFilePutTransport } from "./smallFileLane";
import { ensureDocumentVisibilityTracker, hiddenMsSoFar, readDocumentVisibilityState } from "./documentVisibility";

export interface BlobPutFetch {
	fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
}

/** Browser S3 PUTs fail only after this much time with no increase in uploaded bytes. */
export const CHUNK_STALL_TIMEOUT_MS = 60_000;

/**
 * LSP-19345: the budget for a PUT that has not put a SINGLE byte on the wire yet.
 *
 * `CHUNK_STALL_TIMEOUT_MS` exists (LSP-19070) to protect a genuinely slow uplink: a transfer that is
 * moving bytes may legitimately go quiet for a long time between progress events, and killing it
 * would be the low-bandwidth regression that ticket fixed. A request that has sent NOTHING is a
 * different animal — it is not slow, it never connected — and charging it the mid-transfer budget is
 * what made an offline/online round trip look like a freeze.
 *
 * From the staging repro (conversation afee2e1c…, 2026-09-01): EVERY stall abort in the session
 * logged `bytesSent: 0, elapsedMs: ~60000`. Chunk 2 burned two of those back to back while the
 * session was down to a single upload lane, and 23 backlogged chunks landed within seconds of the
 * second one finally aborting. The reporter's "it takes at least 1 minute before any of the data
 * will go through" is literally this constant.
 *
 * Twenty seconds rather than something tighter, because zero bytes does not always mean a dead
 * socket. S3's REST endpoint is HTTP/1.1, so the browser allows six connections per host, and
 * contact-admin can ask for more than that (FILE_UPLOAD_CONCURRENCY 3 x CHUNK_CONCURRENCY 3): a
 * request waiting its turn in the socket pool fires no upload progress either. It also has to cover
 * DNS + TCP + TLS and the CORS preflight these presigned PUTs trigger. Twenty seconds is far above
 * any of that and still three times cheaper than waiting out the mid-transfer window.
 *
 * The rejection is deliberately IDENTICAL to the mid-transfer stall's — same `abortReason: "stall"`,
 * same TypeError — so `classifyUploadFailure` keeps treating it as a retryable network failure and
 * no lane's terminal policy shifts. Only the budget and the log text change.
 */
export const CHUNK_FIRST_BYTE_TIMEOUT_MS = 20_000;

/**
 * LSP-22375: which S3 endpoint a PUT went to. The transport logged only the path, so "does the regional host
 * fail less than s3-accelerate on cellular?" (LSP-22493) could not be answered from the PUT lines.
 */
export type S3EndpointKind = "accelerate" | "regional" | "other";

export function s3EndpointKindForHost(host: string): S3EndpointKind {
	const lowerHost = host.toLowerCase();
	if (lowerHost.includes(".s3-accelerate.")) return "accelerate";
	if (lowerHost.endsWith(".amazonaws.com") && /(^|\.)s3[.-]/.test(lowerHost)) return "regional";
	return "other";
}

/**
 * LSP-22375: a PUT that failed AFTER the browser reported every byte sent. That only means the bytes reached the
 * phone's socket buffer, but in the 30-day audit ~70% of such chunks were already stored in S3 — so the chunk
 * uploader asks the server before re-sending.
 *
 * Recorded in a side registry, not on the error: the rejection must stay the exact TypeError it always was, own
 * properties included, so `classifyUploadFailure`, every log sink and the persisted retry error are unchanged —
 * and with the kill switch off nothing observable differs at all (break-it 4a).
 */
const fullBodySentFailures = new WeakSet<TypeError>();

export function markFullBodySentFailure<T extends TypeError>(error: T): T {
	fullBodySentFailures.add(error);
	return error;
}

export function isFullBodySentFailure(error: unknown): boolean {
	return error instanceof TypeError && fullBodySentFailures.has(error);
}

function getPresignedUrlLogContext(presignedUrl: string): {
	urlPath: string;
	endpointKind?: S3EndpointKind;
	partNumber?: string;
	uploadId?: string;
} {
	try {
		const parsedUrl = new URL(presignedUrl);
		const partNumber = parsedUrl.searchParams.get("partNumber");
		const uploadId = parsedUrl.searchParams.get("uploadId");
		return {
			urlPath: parsedUrl.pathname,
			endpointKind: s3EndpointKindForHost(parsedUrl.hostname),
			...(partNumber === null ? {} : { partNumber }),
			...(uploadId === null ? {} : { uploadId })
		};
	} catch {
		return { urlPath: "unparseable" };
	}
}

function parseS3ErrorLogFields(responseText: string): {
	s3Code?: string;
	s3Message?: string;
} {
	if (!responseText || typeof DOMParser === "undefined") return {};
	const xml = new DOMParser().parseFromString(responseText, "application/xml");
	return {
		s3Code: xml.querySelector("Code")?.textContent ?? undefined,
		s3Message: xml.querySelector("Message")?.textContent ?? undefined
	};
}

/**
 * Never let a diagnostic log destroy the classification it exists to describe. Both receipt-refusal
 * warns run against a Response that has already misbehaved, where `status` can throw as readily as
 * `ok` did - and an escaping TypeError would replace the `ambiguous` error the caller relies on to
 * retain the local Blob.
 */
function readStatusSafely(response: Response): number | string {
	try {
		return response.status;
	} catch {
		return "unreadable";
	}
}

function isMeaningfulReceiptValue(value: string | null | undefined): value is string {
	return typeof value === "string" && /[^\p{Cc}\p{Cf}\p{Z},]/u.test(value);
}

function safeLog(log: () => void): void {
	try {
		log();
	} catch {
		// Logging must never prevent transport cleanup, promise settlement, or a sibling sweep.
	}
}

/** What asked for a stall verdict: the per-PUT timer, or a wall-clock sweep on a lifecycle wake. */
export type StallSweepTrigger = "visibilitychange" | "pageshow" | "manual";

export interface StallSweepResult {
	inFlight: number;
	aborted: number;
	rearmed: number;
}

/**
 * One in-flight browser PUT as the sweep sees it. `lastProgressAt` is WALL CLOCK: it is set when the
 * request is sent and on every strictly increasing progress event, and it is never touched by the
 * timer, which is exactly why it survives a frozen tab that the timer does not.
 */
interface InFlightS3Put {
	readonly urlContext: Readonly<Record<string, unknown>>;
	lastProgressAt: number;
	hasAbortReason(): boolean;
	currentStallWindowMs(): number;
	abortForStall(trigger: "timer" | StallSweepTrigger, staleForMs: number): boolean;
	rearmStallTimer(remainingMs: number, trigger: StallSweepTrigger): boolean;
}

const inFlightS3Puts = new Set<InFlightS3Put>();

/** Test seam: how many PUTs the sweep would currently consider. */
export function getInFlightS3PutCount(): number {
	return inFlightS3Puts.size;
}

/**
 * LSP-20754: wall-clock stall check over every in-flight PUT.
 *
 * `armStallTimer` protects a PUT with a `setTimeout`, and browsers pause timers while a tab is hidden
 * or the device sleeps. A chunk that stalled while the customer was in another app therefore sat
 * un-aborted until the timer queue resumed — and the only thing that rescued it in production was the
 * file drain's 120 s stale-lease reclaim, reached via a 30 s poll (136 s observed). Hosts call this from
 * their `pageshow` / `visibilitychange→visible` handlers so the verdict is reached in the same tick the
 * tab comes back.
 *
 * A PUT past its budget is aborted through the SAME path the timer uses (`abortReason: "stall"`, same
 * TypeError), so `classifyUploadFailure` still says transient and the chunk retry loop does the retry.
 * A PUT still inside its budget gets its timer re-armed with the REMAINING budget, because a throttled
 * timer that was due in 5 s may otherwise be re-aligned to the next 60 s tick.
 */
export function sweepStalledS3Puts(trigger: StallSweepTrigger, now: number = Date.now()): StallSweepResult {
	const effectiveNow = Number.isFinite(now) ? now : Date.now();
	const entries = Array.from(inFlightS3Puts);
	const result: StallSweepResult = { inFlight: entries.length, aborted: 0, rearmed: 0 };
	for (const entry of entries) {
		if (!inFlightS3Puts.has(entry) || entry.hasAbortReason()) continue;
		const staleForMs = effectiveNow - entry.lastProgressAt;
		const budgetMs = entry.currentStallWindowMs();
		try {
			if (staleForMs >= budgetMs) {
				if (entry.abortForStall(trigger, staleForMs)) result.aborted += 1;
			} else {
				if (entry.rearmStallTimer(budgetMs - staleForMs, trigger)) result.rearmed += 1;
				else result.aborted += 1;
			}
		} catch (sweepError) {
			// One misbehaving XHR must not leave its siblings stuck; log and move on.
			safeLog(() => {
				console.warn("[Upload] S3 PUT stall sweep could not abort an in-flight PUT", {
					...entry.urlContext,
					trigger,
					staleForMs,
					budgetMs,
					error: sweepError instanceof Error ? sweepError.message : String(sweepError)
				});
			});
		}
	}
	// CLAUDE.md -> "Diagnostic logging when fixing silent failures": logged even when nothing was in
	// flight, so a wake that found nothing to do is distinguishable from a wake that never happened.
	safeLog(() => console.info("[Upload] S3 PUT stall sweep", { trigger, ...result, visibilityState: readDocumentVisibilityState() }));
	return result;
}

/**
 * Browser presigned-PUT transport. Total duration is intentionally unbounded; only a lack of byte
 * progress aborts the request. XMLHttpRequest is used because fetch does not expose upload progress.
 */
export function putToS3WithStallTimeout(
	body: Blob | File,
	presignedUrl: string,
	options: {
		progressCallback?: (event: { loaded: number; total: number; percentage: number }) => void;
		cancelSignal?: AbortSignal;
		headers?: Readonly<Record<string, string>>;
		logContext?: Readonly<Record<string, unknown>>;
	}
): Promise<Response> {
	const { progressCallback, cancelSignal, headers, logContext } = options;
	ensureDocumentVisibilityTracker();
	const hiddenMsAtRequestStart = hiddenMsSoFar();
	const visibilityLogFields = (): { visibilityState: ReturnType<typeof readDocumentVisibilityState>; wasHiddenMs: number } => ({
		visibilityState: readDocumentVisibilityState(),
		wasHiddenMs: Math.max(0, hiddenMsSoFar() - hiddenMsAtRequestStart)
	});
	const urlContext = { ...logContext, ...getPresignedUrlLogContext(presignedUrl) };

	return new Promise<Response>((resolve, reject) => {
		if (cancelSignal?.aborted) {
			safeLog(() => {
				console.warn("[Upload] S3 PUT aborted", {
					...urlContext,
					abortReason: "caller-cancel",
					failureReason: "caller-cancel",
					bytesSent: 0,
					bytesTotal: body.size,
					status: 0,
					readyState: 0,
					elapsedMs: 0,
					...visibilityLogFields()
				});
			});
			reject(new DOMException("Aborted", "AbortError"));
			return;
		}

		const xhr = new XMLHttpRequest();
		const requestStartedAt = Date.now();
		let stallTimer: ReturnType<typeof setTimeout> | null = null;
		let bytesSent = 0;
		let abortReason: "stall" | "caller-cancel" | undefined;
		let settled = false;
		// Which of the two budgets was live when the request ended. Reported on every failure so a
		// future incident can tell a dead connection apart from a transfer that went quiet, which is
		// exactly the distinction LSP-19345 had to reconstruct from `bytesSent` by hand.
		let stallWindowMs = CHUNK_FIRST_BYTE_TIMEOUT_MS;
		// LSP-20754: wall-clock instant of the last byte progress (or of send). The sweep compares this
		// against Date.now(); the timer never writes it.
		let lastProgressAt = requestStartedAt;

		const clearStallTimer = () => {
			if (stallTimer !== null) {
				clearTimeout(stallTimer);
				stallTimer = null;
			}
		};
		// LSP-22375: a network failure after the browser reported the whole body sent is registered as full-body.
		const networkFailure = (message: string): TypeError => {
			const failure = new TypeError(message);
			if (body.size > 0 && bytesSent >= body.size) markFullBodySentFailure(failure);
			return failure;
		};
		const claimSettlement = () => {
			if (settled) return false;
			settled = true;
			return true;
		};
		const onAbortSignal = () => {
			if (abortReason !== undefined) return;
			abortReason = "caller-cancel";
			try {
				xhr.abort();
			} catch {
				if (!claimSettlement()) return;
				logFailure("[Upload] S3 PUT aborted", {
					abortReason: "caller-cancel",
					failureReason: "caller-cancel",
					abortCallThrew: true
				});
				cleanup();
				reject(new DOMException("Aborted", "AbortError"));
			}
		};
		const abortForStall = (trigger: "timer" | StallSweepTrigger, staleForMs: number, timerScheduleFailed = false) => {
			if (cancelSignal?.aborted) {
				onAbortSignal();
				return false;
			}
			// The timer and a sweep can both reach a verdict for the same PUT; the first one wins and the
			// second is a no-op rather than a second xhr.abort() and a second log line.
			if (abortReason !== undefined) return false;
			abortReason = "stall";
			clearStallTimer();
			const sawFirstByte = bytesSent > 0;
			safeLog(() => {
				console.warn(
					sawFirstByte
						? `[Upload] aborting stalled S3 PUT after ${CHUNK_STALL_TIMEOUT_MS}ms without progress`
						: `[Upload] aborting S3 PUT after ${CHUNK_FIRST_BYTE_TIMEOUT_MS}ms without sending a single byte`,
					{
						...urlContext,
						trigger,
						staleForMs,
						stallWindowMs,
						bytesSent,
						...(timerScheduleFailed ? { timerScheduleFailed: true } : {}),
						...visibilityLogFields()
					}
				);
			});
			try {
				xhr.abort();
			} catch {
				if (claimSettlement()) {
					logFailure("[Upload] S3 PUT aborted", {
						abortReason: "stall",
						failureReason: "stall",
						abortCallThrew: true
					});
					cleanup();
					reject(networkFailure("S3 PUT stalled without upload progress"));
				}
			}
			return true;
		};
		// `windowMs` defaults to the full budget for the live phase; the sweep passes the REMAINING
		// budget when it re-arms a PUT that is still inside it, and that call must not move
		// `lastProgressAt` (no byte arrived).
		const armStallTimer = (
			windowMs?: number,
			requestPhase: "pre-send" | "in-flight" = "in-flight",
			fallbackTrigger: "timer" | StallSweepTrigger = "timer"
		) => {
			clearStallTimer();
			// Re-read `bytesSent` on every arming rather than latching a window at send: the progress
			// handler assigns it before re-arming, so the budget widens to the mid-transfer one the moment
			// the first byte lands and never narrows again.
			stallWindowMs = bytesSent > 0 ? CHUNK_STALL_TIMEOUT_MS : CHUNK_FIRST_BYTE_TIMEOUT_MS;
			if (windowMs === undefined) lastProgressAt = Date.now();
			try {
				stallTimer = setTimeout(() => abortForStall("timer", Date.now() - lastProgressAt), windowMs ?? stallWindowMs);
				return true;
			} catch (timerError) {
				if (requestPhase === "pre-send") {
					throw new TypeError(timerError instanceof Error ? timerError.message : String(timerError));
				}
				abortForStall(fallbackTrigger, Date.now() - lastProgressAt, true);
				return false;
			}
		};
		const inFlightEntry: InFlightS3Put = {
			urlContext,
			get lastProgressAt() {
				return lastProgressAt;
			},
			set lastProgressAt(value: number) {
				lastProgressAt = value;
			},
			hasAbortReason: () => abortReason !== undefined,
			currentStallWindowMs: () => (bytesSent > 0 ? CHUNK_STALL_TIMEOUT_MS : CHUNK_FIRST_BYTE_TIMEOUT_MS),
			abortForStall,
			rearmStallTimer: (remainingMs: number, trigger: StallSweepTrigger) =>
				armStallTimer(Math.max(0, remainingMs), "in-flight", trigger)
		};
		const cleanup = () => {
			clearStallTimer();
			inFlightS3Puts.delete(inFlightEntry);
			cancelSignal?.removeEventListener("abort", onAbortSignal);
		};
		const logFailure = (label: string, extra: Record<string, unknown>) => {
			safeLog(() => {
				console.warn(label, {
					...urlContext,
					...extra,
					bytesSent,
					bytesTotal: body.size,
					status: xhr.status,
					readyState: xhr.readyState,
					stallWindowMs,
					elapsedMs: Date.now() - requestStartedAt,
					...visibilityLogFields()
				});
			});
		};

		cancelSignal?.addEventListener("abort", onAbortSignal, { once: true });
		xhr.upload.addEventListener("progress", (event: ProgressEvent) => {
			if (settled) return;
			if (event.loaded <= bytesSent) return;
			bytesSent = event.loaded;
			armStallTimer();
			if (progressCallback) {
				const total = event.lengthComputable && event.total > 0 ? event.total : body.size;
				progressCallback({
					loaded: event.loaded,
					total,
					percentage: Math.round((event.loaded / total) * 100)
				});
			}
		});
		xhr.addEventListener("load", () => {
			if (!claimSettlement()) return;
			cleanup();
			const succeeded = xhr.status >= 200 && xhr.status < 300;
			try {
				if (!succeeded) {
					logFailure("[Upload] S3 PUT returned a non-2xx response", {
						failureReason: "http-error",
						status: xhr.status,
						statusText: xhr.statusText,
						...parseS3ErrorLogFields(xhr.responseText)
					});
				} else {
					safeLog(() => {
						console.info("[Upload] S3 PUT completed", {
							...urlContext,
							status: xhr.status,
							readyState: xhr.readyState,
							bytesSent,
							bytesTotal: body.size,
							elapsedMs: Date.now() - requestStartedAt,
							...visibilityLogFields()
						});
					});
				}
				// ETag is the only receipt header read. `x-upload-receipt` was speculative: no service in
				// this platform has ever SET it on any response, and this PUT is answered by S3, which
				// will not start emitting a header we invented. (`setDefaultCorsPolicy` in contact-api
				// does list it under `ExposeHeaders` - LSP-19627 - but exposing a header the origin never
				// sends changes nothing.)
				//
				// Asking a cross-origin response for a header its CORS does not expose makes Chrome log
				// `Refused to get unsafe header "x-upload-receipt"` at error level on every single upload
				// (LSP-19478). That log comes from the browser itself, so the catch below cannot suppress
				// it; not asking is the only way to silence it.
				const responseHeaders = new Headers();
				try {
					const retryAfter = xhr.getAllResponseHeaders().match(/(?:^|\r?\n)retry-after:[ \t]*([^\r\n]*)/i)?.[1];
					if (retryAfter) responseHeaders.set("Retry-After", retryAfter.trim());
				} catch {
					// An unreadable optional hint leaves the normal backoff in place.
				}
				try {
					const rawEtag = xhr.getResponseHeader("ETag");
					const trimmedEtag = rawEtag?.trim();
					if (isMeaningfulReceiptValue(trimmedEtag)) {
						responseHeaders.set("ETag", trimmedEtag);
					} else if (succeeded && rawEtag !== null) {
						// Present but unusable, and this is the only place that fact survives: the Response
						// built below carries an ETag only when it is meaningful, so by the time the receipt
						// gate looks, a garbage value is indistinguishable from a header that never arrived.
						// Absent is deliberately NOT logged here - the gate already reports that, and this is
						// the common case, so logging it too would re-create the double-logging LSP-19478
						// exists to remove.
						safeLog(() => {
							console.warn("[Upload] S3 PUT returned an ETag that cannot serve as a receipt", {
								...urlContext,
								status: xhr.status,
								etagLength: rawEtag.length
							});
						});
					}
				} catch (etagReadError) {
					// A malformed header accessor must not fail an otherwise complete PUT. Worth a line
					// even so: ETag is now the sole receipt, so on a 2xx this is the difference between a
					// completed upload and an ambiguous park, and it is otherwise invisible. Only on a 2xx -
					// a non-2xx already logged itself above, and a missing receipt is moot there.
					if (succeeded) {
						safeLog(() => {
							console.warn("[Upload] S3 PUT response ETag could not be read", {
								...urlContext,
								status: xhr.status,
								readError: etagReadError instanceof Error ? etagReadError.message : String(etagReadError)
							});
						});
					}
				}
				const response = new Response(xhr.responseText || null, {
					status: xhr.status,
					statusText: xhr.statusText,
					headers: responseHeaders
				});
				resolve(response);
			} catch (responseError) {
				logFailure("[Upload] S3 PUT response could not be assembled", {
					failureReason: "response-construction",
					responseError: responseError instanceof Error ? responseError.message : String(responseError)
				});
				reject(responseError instanceof Error ? responseError : new TypeError(String(responseError)));
			}
		});
		xhr.addEventListener("error", () => {
			if (!claimSettlement()) return;
			cleanup();
			logFailure("[Upload] S3 PUT XHR error", { failureReason: "xhr-error" });
			reject(networkFailure("Network request failed"));
		});
		xhr.addEventListener("abort", () => {
			if (!claimSettlement()) return;
			cleanup();
			logFailure("[Upload] S3 PUT aborted", {
				abortReason: abortReason ?? "xhr-abort",
				failureReason: abortReason ?? "xhr-abort"
			});
			reject(
				abortReason === "stall"
					? networkFailure("S3 PUT stalled without upload progress")
					: new DOMException("Aborted", "AbortError")
			);
		});
		xhr.addEventListener("timeout", () => {
			if (!claimSettlement()) return;
			cleanup();
			logFailure("[Upload] S3 PUT timed out", { abortReason: "xhr-timeout", failureReason: "xhr-timeout" });
			reject(new DOMException("Aborted", "AbortError"));
		});

		try {
			xhr.open("PUT", presignedUrl);
			for (const [name, value] of Object.entries(headers ?? {})) xhr.setRequestHeader(name, value);
			safeLog(() => {
				console.info("[Upload] S3 PUT started", {
					...urlContext,
					bytesSent: 0,
					bytesTotal: body.size,
					status: xhr.status,
					readyState: xhr.readyState,
					elapsedMs: 0,
					...visibilityLogFields()
				});
			});
			inFlightS3Puts.add(inFlightEntry);
			armStallTimer(undefined, "pre-send");
			xhr.send(body);
		} catch (openError) {
			if (!claimSettlement()) return;
			cleanup();
			logFailure("[Upload] S3 PUT setup failed", {
				failureReason: "setup-error",
				errorName: openError instanceof Error ? openError.name : undefined,
				errorMessage: openError instanceof Error ? openError.message : String(openError)
			});
			reject(openError instanceof Error ? openError : new TypeError(String(openError)));
		}
	});
}

export class BlobPutHttpError extends Error {
	readonly status: number;
	readonly code?: string;
	readonly responseBody?: string;
	readonly retryAfter?: string | null;

	constructor(status: number, responseBody?: string, retryAfter?: string | null) {
		super(`S3 PUT ${status}`);
		this.name = "BlobPutHttpError";
		this.status = status;
		this.responseBody = responseBody;
		this.retryAfter = retryAfter;
		this.code =
			/<Code>\s*([^<]+?)\s*<\/Code>/i.exec(responseBody ?? "")?.[1].trim() ||
			(/Request has expired|ExpiredToken/i.test(responseBody ?? "") ? "ExpiredToken" : undefined);
	}
}

const hasContentTypeHeader = (headers: Readonly<Record<string, string>> | undefined): boolean =>
	Object.keys(headers ?? {}).some((name) => name.toLowerCase() === "content-type");

/**
 * The Content-Type a presigned PUT's signature actually covers, or `undefined` when it covers none.
 *
 * The SigV2 presigner (`aws-sdk` v2 `getSignedUrl`, used by every `s3.ts` `getSignedUrl("putObject")`
 * call) copies the signed headers into the query string, so `?Content-Type=` is present exactly when
 * `ContentType` was signed and absent when it was signed as `null`. `shared/frontend/ui`'s S3
 * transport already reads the same parameter for the same reason.
 *
 * SigV4 (`@aws-sdk/s3-request-presigner`, multipart `UploadPartCommand`) instead lists what it signed
 * in `X-Amz-SignedHeaders`; those parts are presigned with no ContentType, but treat a signed-headers
 * list that names content-type as "covered, value unknown" and leave the body alone rather than guess.
 */
function signedContentType(url: string): string | undefined | "unknown" {
	let parameters: URLSearchParams;
	try {
		parameters = new URL(url).searchParams;
	} catch {
		return "unknown";
	}
	const fromQuery = parameters.get("Content-Type");
	if (fromQuery) return fromQuery;
	return /(^|;)\s*content-type\s*(;|$)/i.test(parameters.get("X-Amz-SignedHeaders") ?? "") ? "unknown" : undefined;
}

/**
 * Aligns the request's Content-Type with the one the URL's signature covers — in BOTH directions.
 *
 * - Signature covers a Content-Type (legacy photo/file presigns, `/uploads` recording chunk-stream):
 *   send exactly that. Relying on the payload Blob's own type instead is what turns a
 *   `video/webm;codecs=vp8,opus` Blob into a request S3 cannot verify.
 * - Signature covers none (`s3.ts` `createRecordingPreSignedUrl` with `ContentType: null` — the
 *   recording chunk lane — and SigV4 `UploadPartCommand` parts): the request must carry no
 *   Content-Type at all. `fetch()` derives one from a typed Blob when the header is absent, so the
 *   type is stripped. contact-mobile already holds this half of the contract explicitly with
 *   `NoContentTypeByteArrayBody` ("the server signs those URLs with ContentType=null, so any
 *   Content-Type header changes the canonical request → 403").
 *
 * `slice()` re-views the same bytes with an empty type, so stripping costs no copy.
 */
function alignContentTypeWithSignature(
	url: string,
	body: Blob,
	headers: Readonly<Record<string, string>> | undefined
): { body: Blob; headers: Readonly<Record<string, string>> | undefined } {
	// A caller-supplied Content-Type is the server-minted one (`/uploads` target envelopes). It wins
	// outright: it is what the signature covers, and re-deriving it here could only disagree.
	if (hasContentTypeHeader(headers)) return { body, headers };
	const signed = signedContentType(url);
	if (signed === "unknown") return { body, headers };
	if (signed) return { body, headers: { ...headers, "Content-Type": signed } };
	return { body: body.type ? body.slice(0, body.size) : body, headers };
}

/** Browser transport shared by both lanes. The Blob is passed directly as the request body. */
export class BlobPutTransport implements ChunkStreamPutTransport, SmallFilePutTransport {
	private readonly fetcher?: BlobPutFetch;

	constructor(fetcher?: BlobPutFetch) {
		this.fetcher = fetcher;
	}

	async put(
		url: string,
		body: Blob,
		options: {
			signal: AbortSignal;
			onProgress?: (bytesUploaded: number) => void;
			headers?: Readonly<Record<string, string>>;
			logContext?: Readonly<Record<string, unknown>>;
			requireServerReceipt?: boolean;
		}
	): Promise<{ status: number }> {
		if (!(body instanceof Blob) || body.size <= 0) throw new Error("Cannot PUT an empty Blob");
		// Applied to BOTH transports on purpose. LSP-19070 added the stall-timeout path below, and it is
		// the production one - `fetcher` is the injected test/override seam. Aligning only the fetch branch
		// would leave the real XHR PUT sending the Blob's own type against a signature that covers a
		// different one (or none), which is exactly the SignatureDoesNotMatch this alignment exists to fix.
		const aligned = alignContentTypeWithSignature(url, body, options.headers);
		const response = this.fetcher
			? await this.fetcher.fetch(url, {
					method: "PUT",
					body: aligned.body,
					signal: options.signal,
					headers: aligned.headers
				})
			: await putToS3WithStallTimeout(aligned.body, url, {
					cancelSignal: options.signal,
					headers: aligned.headers,
					logContext: options.logContext,
					progressCallback: options.onProgress ? ({ loaded }) => options.onProgress?.(loaded) : undefined
				});
		let responseOk: boolean;
		let hasMeaningfulServerReceipt = false;
		if (options.requireServerReceipt) {
			try {
				responseOk = response.ok;
				// ETag only - see the header read in `putToS3WithStallTimeout` above (LSP-19478). An ETag
				// that arrived but was unusable is reported at that read site, not here: this Response only
				// ever carries the header when it is already meaningful.
				hasMeaningfulServerReceipt = !responseOk || isMeaningfulReceiptValue(response.headers?.get("ETag"));
			} catch (inspectionError) {
				safeLog(() => {
					console.warn("[Upload] Direct PUT response could not be inspected for a receipt; retaining the local Blob", {
						...options.logContext,
						...getPresignedUrlLogContext(url),
						status: readStatusSafely(response),
						inspectionError: inspectionError instanceof Error ? inspectionError.message : String(inspectionError)
					});
				});
				throw Object.assign(new Error("Direct PUT completed without a server receipt; retaining the local Blob"), {
					ambiguous: true
				});
			}
		} else {
			responseOk = response.ok;
		}
		if (!responseOk) {
			const responseBody = typeof response.text === "function" ? await response.text().catch(() => undefined) : undefined;
			const retryAfter = typeof response.headers?.get === "function" ? response.headers.get("Retry-After") : undefined;
			throw new BlobPutHttpError(response.status, responseBody, retryAfter);
		}
		if (options.requireServerReceipt && !hasMeaningfulServerReceipt) {
			safeLog(() => {
				console.warn("[Upload] Direct PUT answered 2xx with no meaningful ETag; retaining the local Blob", {
					...options.logContext,
					...getPresignedUrlLogContext(url),
					status: readStatusSafely(response)
				});
			});
			throw Object.assign(new Error("Direct PUT completed without a server receipt; retaining the local Blob"), {
				ambiguous: true
			});
		}
		options.onProgress?.(body.size);
		return { status: response.status };
	}
}

export const createBlobPutTransport = (fetcher?: BlobPutFetch): BlobPutTransport => new BlobPutTransport(fetcher);
